import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Heading, Paragraph, Tag } from '@digdir/designsystemet-react';
import type { DataSource, RiskResult } from '../../../api/workEnvironmentTypes';
import { sourcesFor } from '../dataSourcesView';
import {
  AreaFilter,
  areaName,
  byRisk,
  filteredAreas,
  isSameFilter,
  levelCounts,
  matrixCells,
  toggleFilter,
} from '../riskView';
import { RiskAreaItem } from './RiskAreaItem';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import classes from '../WorkEnvironmentSurveyRisk.module.css';

type Props = { risk: Extract<RiskResult, { status: 'assessed' }>; sources: DataSource[] };

const LIKELIHOODS = [1, 2, 3, 4, 5];

const LevelLabel = ({ level, name, count }: { level: string; name: string; count: number }) => (
  <>
    <span aria-hidden="true" className={classes.dot} data-level={level} />
    <span className="ds-paragraph" data-size="sm">
      {name}
    </span>
    <span className="ds-label">{count}</span>
  </>
);

export const RiskMatrixCard = ({ risk, sources }: Props): ReactElement => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<AreaFilter>(null);
  const shown = byRisk(filteredAreas(risk.areas, filter));
  const levelName = (level: string) => t(`workEnvironmentRisk.matrix.levels.${level}`);

  return (
    <section id="matrix" aria-labelledby="matrix-heading" className={process.card}>
      <div className={process.cardBody}>
        <div className={process.sideHeading}>
          <Heading level={2} data-size="sm" id="matrix-heading">
            {t('workEnvironmentRisk.matrix.title')}
          </Heading>
          <Paragraph data-size="sm" className={process.subtle}>
            {t('workEnvironmentRisk.matrix.intro')}
          </Paragraph>
        </div>
        <div className={classes.matrixRow}>
          <figure className={classes.matrixFigure}>
            <span className={`ds-label ${classes.axisLabel} ${classes.axisVertical}`} data-size="xs">
              {t('workEnvironmentRisk.matrix.consequence')}
            </span>
            <table className={classes.matrix}>
              <caption className="ds-sr-only">{t('workEnvironmentRisk.matrix.title')}</caption>
              <tbody>
                {matrixCells(risk.areas, risk.levels).map((row) => (
                  <tr key={row[0].consequence}>
                    <th scope="row">{row[0].consequence}</th>
                    {row.map((cell) => {
                      const next = {
                        type: 'cell',
                        consequence: cell.consequence,
                        likelihood: cell.likelihood,
                      } as const;
                      const label = t('workEnvironmentRisk.matrix.cell', {
                        consequence: cell.consequence,
                        likelihood: cell.likelihood,
                        level: levelName(cell.level),
                        count: cell.count,
                      });
                      return (
                        <td key={cell.likelihood}>
                          {cell.count > 0 ? (
                            <button
                              type="button"
                              className={classes.cell}
                              data-level={cell.level}
                              aria-pressed={isSameFilter(filter, next)}
                              aria-label={label}
                              onClick={() => setFilter(toggleFilter(filter, next))}
                            >
                              <span className={classes.count} data-level={cell.level} aria-hidden="true">
                                {cell.count}
                              </span>
                            </button>
                          ) : (
                            <span className={classes.cell} data-level={cell.level}>
                              <span className="ds-sr-only">{label}</span>
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr>
                  <td />
                  {LIKELIHOODS.map((likelihood) => (
                    <th key={likelihood} scope="col">
                      {likelihood}
                    </th>
                  ))}
                </tr>
              </tbody>
            </table>
            <span className={`ds-label ${classes.axisLabel} ${classes.axisHorizontal}`} data-size="xs">
              {t('workEnvironmentRisk.matrix.likelihood')}
            </span>
          </figure>
          <ul className={classes.levels}>
            {levelCounts(risk.areas).map(({ level, count }) => {
              const next = { type: 'level', level } as const;
              return (
                <li key={level}>
                  {count > 0 ? (
                    <button
                      type="button"
                      className={classes.levelButton}
                      aria-pressed={isSameFilter(filter, next)}
                      onClick={() => setFilter(toggleFilter(filter, next))}
                    >
                      <LevelLabel level={level} name={levelName(level)} count={count} />
                    </button>
                  ) : (
                    <span className={classes.levelButton}>
                      <LevelLabel level={level} name={levelName(level)} count={count} />
                    </span>
                  )}
                </li>
              );
            })}
            {filter && (
              <li>
                <Button variant="tertiary" data-size="sm" onClick={() => setFilter(null)}>
                  {t('workEnvironmentRisk.matrix.showAll')}
                </Button>
              </li>
            )}
          </ul>
        </div>
      </div>
      <div className={classes.listHeading}>
        <Paragraph data-size="sm" className={process.subtle} aria-live="polite">
          {filter
            ? t('workEnvironmentRisk.matrix.listFiltered', { shown: shown.length, total: risk.areas.length })
            : t('workEnvironmentRisk.matrix.listAll', {
                count: risk.areas.length,
                responses: risk.responses,
                min: risk.minResponses,
              })}
        </Paragraph>
      </div>
      <ul className={classes.areas}>
        {shown.map((area) => (
          <li key={area.key}>
            <RiskAreaItem area={area} sources={sourcesFor(area.key, sources)} />
          </li>
        ))}
      </ul>
      {risk.unassessed.length > 0 && (
        <div className={process.cardFooter}>
          <div className={classes.areaBody}>
            <Heading level={3} data-size="2xs">
              {t('workEnvironmentRisk.area.tooFewTitle')}
            </Heading>
            <Paragraph data-size="sm" className={process.subtle}>
              {t('workEnvironmentRisk.area.tooFew', { min: risk.minResponses })}
            </Paragraph>
            <ul className={classes.followUpList}>
              {risk.unassessed.map((area) => (
                <li key={area.key} className="ds-paragraph" data-size="sm">
                  {areaName(area, t)}
                  {area.requiresFollowUp && (
                    <>
                      {' '}
                      <Tag data-size="sm" data-color="danger">
                        {t('workEnvironmentRisk.area.requiresFollowUp')}
                      </Tag>
                    </>
                  )}
                  {area.skipped !== null && ` ${t('workEnvironmentRisk.area.skipped', { count: area.skipped })}`}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
};
